import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
//import { enqueueSnackbar } from 'notistack'
import moment from 'moment-timezone'
import parseHtml from 'html-react-parser'

//import { Typography, Switch, Stack } from '@mui/material'
import { TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'

import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import EditIcon from '@mui/icons-material/Edit'

import { AppContext } from '/providers/app-context.js'

import { getTemplateData, renameTemplate } from '/providers/templates-api' // setGoogleBarcodeHandling
import ClickToCopy from '/components/click-to-copy'

import Stamp from './scanner-stamp'
import Loyalty from './scanner-loyalty'
import Gift from './scanner-gift'
import StyledPaper from '../paper.jsx'
import { getCurrentSyncCounts, getSyncIndicator, hasCurrentSyncAnswer } from './sync-state-utils'

const DetailSummary = ({ template }) => {

  const navigate = useNavigate()

  const { apiSource, apiKey, refreshTemplate, setProcessing } = useContext(AppContext)
  const [templateName, setTemplateName] = useState(template.name)
  const [editFormVisible, setEditFormVisible] = useState(false)
  const [savingName, setSavingName] = useState(false)
  const isTemplateNameValid = templateName.trim().length >= 3
  const [templateData, setTemplateData] = useState()
  const syncCounts = getCurrentSyncCounts(template)
  const syncIndicator = getSyncIndicator(template)
  const syncStatus = hasCurrentSyncAnswer(template)
    ? <><AppleIcon />{syncCounts.apple} <AndroidIcon className='ml-2' />{syncCounts.google} <span className='ml-2'>Unique {syncCounts.unique}</span></>
    : syncIndicator
      ? <span className={syncIndicator.tone === 'warning' ? 'orange' : 'link'}>{syncIndicator.label}</span>
      : <span className='grey'>No sync required</span>

  useEffect(() => {
    if (!template) navigate("/templates/") // Navigate back to /template/
    getTemplate()
  }, [])

  const getTemplate = async () => {
    const td = await getTemplateData({ apiSource, apiKey, templateId: template.template_id })
    setTemplateData(td.template)
  }

  useEffect(() => {
    if (template?.name) setTemplateName(template.name)
  }, [template?.name])

  const saveTemplateName = async () => {
    setProcessing(true)
    try {
      await renameTemplate({ apiSource, apiKey, templateId: template.template_id, name: templateName })
      // TODO handle error
      await refreshTemplate(template.template_id)
    } finally {
      setProcessing(false)
    }
  }

  const openEditDialog = () => {
    setTemplateName(template.name)
    setEditFormVisible(true)
  }

  const closeEditDialog = () => {
    setTemplateName(template.name)
    setSavingName(false)
    setEditFormVisible(false)
  }

  const handleSaveTemplateName = async () => {
    if (!isTemplateNameValid || savingName) return
    setSavingName(true)
    try {
      await saveTemplateName()
      setEditFormVisible(false)
    } finally {
      setSavingName(false)
    }
  }

  return (
    <div className="max-w-[600px]">
      <StyledPaper>
        {dataItem("Template id", <ClickToCopy>{template.template_id}</ClickToCopy>)}
        <div className='flex justify-between w-full gap-10 mb-3'>
          <div className='text-nowrap'>Template Name</div>
          <div className='flex flex-row gap-2'>
            <div className='font-bold'>{template.name}</div>
            <EditIcon className="ml-2 cursor-pointer" onClick={openEditDialog} />
          </div>
        </div>
        <br />
        {dataItem("Status", template.archived ? <span className='red'>Archived</span> : <span className='green'>Active</span>)}
        <br />
        {dataItem("Pass type", template.pass_type)}
        {dataItem("Description", parseHtml(template.description))}
        <br />
        {dataItem("Created", moment(template.creation_timestamp, "YYYYMMDDHHmmss").format("DD MMM YYYY HH:mm"))}
        {dataItem("last modified", moment(template.last_modified_timestamp, "YYYYMMDDHHmmss").format("DD MMM YYYY HH:mm"))}
        <br />
        {dataItem("Test passes issued", template.test_passes_issued)}
        {dataItem("Live passes issued", template.live_passes_issued)}
        {dataItem("Active live passes", template.num_live_active_passes)}
        {dataItem("Expired live passes", template.num_live_expired_passes)}
        <br />
        {dataItem("Synchronisation", syncStatus)}
        <br />
        {dataItem("Smartix pass type", template.pass_type_id)}
        {dataItem("Apple pass type identifier", template.apple_pass_type_identifier)}
        {dataItem("Apple pass type / sub-type", template.apple_pass_type + (template.apple_sub_type ? (" / " + template.apple_sub_type) : ""))}
        {dataItem("Apple certificate type", templateData?.apple_certificate?.certificate_type)}
        {dataItem("Apple team ID", templateData?.apple_certificate?.apple_team_id)}
        <br/>
        {dataItem("Google pass type / sub-type", template.google_pass_type + (template.google_sub_type ? (" / " + template.google_sub_type) : ""))}
        {dataItem("Google certificate type", templateData?.google_certificate?.certificate_type)}
        {dataItem("Google certificate name", templateData?.google_certificate?.certificate_name)}
        {dataItem("Google issuer ID", templateData?.google_certificate?.google_issuer_id)}
        <br />
        {dataItem("Smartix scanner app compatible", template.managed_pass_template ? "Yes" : "No")}
      </StyledPaper>

      <ScannerDetailsWrapper template={template} templateData={templateData} setTemplateData={setTemplateData}/>

      <Dialog open={editFormVisible} onClose={closeEditDialog} fullWidth>
        <DialogTitle>Edit template name</DialogTitle>
        <DialogContent>
          <TextField
            label="Template Name"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            fullWidth
            autoFocus
            margin="dense"
            error={!isTemplateNameValid}
            helperText={!isTemplateNameValid ? 'Name must be at least 3 characters.' : ' '}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeEditDialog} disabled={savingName}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplateName} disabled={!isTemplateNameValid || savingName}>{savingName ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

/*
      {dataItem("Disable Google barcodes on expiry *", <DisableBarcodeSwitch template={template} disableBarcode={disableBarcode} toggleGoogleBarcodeDisable={toggleGoogleBarcodeDisable} />)}
      <div>* May incur additional charges</div>
      <br />
*/

export default DetailSummary

/*
const DisableBarcodeSwitch = ({ template, disableBarcode, toggleGoogleBarcodeDisable }) => {

  if (template.archived) return disableBarcode === 1 ? "Yes" : "No"

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Typography>No</Typography>
      <Switch checked={disableBarcode}
        onChange={toggleGoogleBarcodeDisable} />
      <Typography>Yes</Typography>
    </Stack>
  )
}
*/

const dataItem = (title, value) => <div className='flex justify-between w-full gap-10 mb-3'><div className='text-nowrap'>{title}</div><div className='font-bold'>{value}</div></div>

const ScannerDetailsWrapper = ({ template, templateData, setTemplateData }) => {
  if (template.managed_pass_template === 1) {
    return <ScannerDetails templateData={templateData} setTemplateData={setTemplateData}/>
  }
}

const ScannerDetails = ({ templateData,setTemplateData }) => {

  if (templateData && templateData.pass_type_id === "managed_stamp") {
    return (
      <div className='mt-10'>
        <Stamp template={templateData} setTemplate={setTemplateData} />
      </div>
    )
  }

  if (templateData && templateData.pass_type_id === "managed_loyalty") {
    return (
      <div className='mt-10'>
        <Loyalty template={templateData} setTemplate={setTemplateData} />
      </div>
    )
  }

  if (templateData && templateData.pass_type_id === "managed_gift") {
    return (
      <div className='mt-10'>
        <Gift template={templateData} setTemplate={setTemplateData} />
      </div>
    )
  }
}
